import { z } from 'zod';
import { freeText, personName } from './text-policy.js';
import { pageQuerySchema } from './http.js';
import { multiLanguageContentSchema } from './i18n.js';
import { optionalE164Schema } from './phone.js';
/**
 * The three states a human or a redemption WROTE, each with an actor and a timestamp.
 *
 * Must stay identical — same members, SAME ORDER — to the `VisitorInviteStatus` Prisma
 * enum in packages/db/prisma/schema.prisma (ADR-0011), proven by the lock-step test in
 * packages/db/src/visitor-invite-enums.test.ts.
 *
 * `EXPIRED` IS DELIBERATELY ABSENT. An invite is expired when `status = PENDING AND
 * expiresAt <= now` — a fact about the clock that nothing ever wrote to a row. Storing
 * it would require a scheduled job, which docs/002 "Runtime portability" makes actively
 * awkward (nothing may run after a response, and there is no scheduler). `DevicePairingCode`
 * makes the same call more strongly: it carries no status column at all.
 */
export const VISITOR_INVITE_STATUSES = ['PENDING', 'REDEEMED', 'CANCELLED'];
export const visitorInviteStatusSchema = z.enum(VISITOR_INVITE_STATUSES);
/**
 * What a READER sees: the three stored states plus the derived one. This is the union on
 * `InviteDTO.status` and the union a list filter accepts — never the union written to a row.
 * Keeping the two apart in the type system is what stops `EXPIRED` from ever reaching a
 * Prisma `data:` payload.
 */
export const VISITOR_INVITE_VIEW_STATUSES = ['PENDING', 'REDEEMED', 'CANCELLED', 'EXPIRED'];
export const visitorInviteViewStatusSchema = z.enum(VISITOR_INVITE_VIEW_STATUSES);
/**
 * 8 characters of Crockford base32 — `0-9` plus `A-Z` minus `I`, `L`, `O` and `U`
 * (INV-05). No `I`/`L`/`O` because a visitor reads the code off a phone screen and would
 * confuse them with `1` and `0`; no `U` by Crockford's own rule. 32^8 ≈ 1.1 x 10^12, which
 * is why six digits (the pairing code, typed under supervision within 15 minutes) is not
 * the right precedent for a code emailed days ahead.
 *
 * This pattern is the WIRE shape only. The generator and the hash live in
 * apps/api/src/modules/apps/visitor/invite-code.ts (S11-TASK-002) — never here: contracts
 * is dependency-free and browser-safe and must not import `node:crypto`.
 */
export const INVITE_CODE_PATTERN = /^[0-9A-HJKMNP-TV-Z]{8}$/;
/** Mirrors `VisitorInviteSeriesFrequency` (packages/db, S48-lh45-TASK-086 lock-step test). ADD ONLY. */
export const INVITE_RECURRENCE_FREQUENCIES = ['DAILY', 'WEEKLY'];
/**
 * Input bounds on ONE request — validation, like `MAX_PAGE_SIZE`. They are NOT plan limits and
 * not PlanEntitlement keys.
 */
export const INVITE_SERIES_MIN_OCCURRENCES = 2;
/** Input validation, not a plan limit (see above). */
export const INVITE_SERIES_MAX_OCCURRENCES = 12;
/** Input validation, not a plan limit (see above). */
export const INVITE_MAX_CO_HOSTS = 5;
export const inviteRecurrenceSchema = z.object({
    frequency: z.enum(INVITE_RECURRENCE_FREQUENCIES),
    /** Total visits including the first. */
    occurrences: z.number().int().min(INVITE_SERIES_MIN_OCCURRENCES).max(INVITE_SERIES_MAX_OCCURRENCES),
});
/** Person ids of active employees/contractors; the server proves each (S48-lh45-TASK-091). Duplicates are collapsed server-side. */
export const inviteCoHostIdsSchema = z.array(z.string().trim().min(1).max(64)).max(INVITE_MAX_CO_HOSTS);
/** A co-host as an invite shows it. Name only — never contact details (S7). */
export const inviteCoHostSchema = z.object({
    personId: z.string().min(1),
    displayName: z.string().min(1),
});
/** Where this invite sits in its series. `occurrenceIndex` is 1-based. */
export const inviteSeriesSummarySchema = z.object({
    id: z.string().min(1),
    frequency: z.enum(INVITE_RECURRENCE_FREQUENCIES),
    occurrenceIndex: z.number().int().min(1),
    occurrenceCount: z.number().int().min(INVITE_SERIES_MIN_OCCURRENCES),
});
/** One occurrence's ONE-TIME credentials, returned only by create (same rule as `issuedInviteDTOSchema.code`). */
export const issuedInviteOccurrenceSchema = z.object({
    inviteId: z.string().min(1),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    code: z.string().regex(INVITE_CODE_PATTERN),
    passUrl: z.string().url(),
});
/**
 * `POST /api/web/invites`.
 *
 * There is NO `flowId` and NO `code`, both deliberately. The flow is re-resolved from the
 * device's location at arrival (`service.ts#createVisit`), so an invite that pinned one
 * would be a second flow-selection path and could pin a version since superseded. The code
 * is minted server-side; a client-supplied code would be a client-chosen secret.
 *
 * `expectedFrom`/`expectedTo` are ISO 8601 INSTANTS, half-open `[from, to)`, converted from
 * the operator's local days in the browser — the exact convention S10 fixed for the visitor
 * log's date filter. They are INFORMATIONAL: `expiresAt` is the security boundary and is
 * derived server-side, never accepted from the client.
 */
export const createInviteSchema = z.object({
    visitorFirstName: personName({ max: 100 }),
    visitorLastName: personName({ max: 100 }),
    visitorEmail: z.string().trim().email().max(254),
    // E.164 only — see `phone.ts`. The read schema below stays a plain string.
    visitorPhone: optionalE164Schema,
    hostPersonId: z.string().min(1),
    locationId: z.string().min(1),
    /**
     * The visitor type the admin chose — REQUIRED. The server proves it belongs to this tenant,
     * is active, and is offered at `locationId` (the same rule a kiosk check-in's type obeys),
     * so an id valid for another lobby is refused rather than stored.
     */
    visitorTypeId: z.string().trim().min(1).max(64),
    purpose: freeText({ max: 280 }).nullable().default(null),
    note: freeText({ max: 1000, multiLine: true }).nullable().default(null),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    /**
     * S41: email the visitor their Visit Pass. OPTIONAL on the wire and treated as `true` when
     * absent, so every existing caller keeps compiling and the default is the documented one.
     */
    sendEmail: z.boolean().optional(),
    /** S48-lh45 Phase 4b. Absent = none. */
    coHostPersonIds: inviteCoHostIdsSchema.optional(),
    /** S48-lh45 Phase 4b. Absent or null = a one-off invite. */
    recurrence: inviteRecurrenceSchema.nullable().optional(),
});
/**
 * `NOT_STARTED` — the visitor has never saved anything. `IN_PROGRESS` — something saved,
 * something required still missing. `READY` — every required question answered and every
 * required agreement signed (computed, never posted).
 */
export const INVITE_PRE_REGISTRATION_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'READY'];
export const invitePreRegistrationStatusSchema = z.enum(INVITE_PRE_REGISTRATION_STATUSES);
/** The admin list's "Ready / 2 of 3" badge. Counts are a snapshot taken at the visitor's last save. */
export const invitePreRegistrationSummarySchema = z.object({
    status: invitePreRegistrationStatusSchema,
    answeredCount: z.number().int().min(0),
    requiredCount: z.number().int().min(0),
    signedCount: z.number().int().min(0),
    agreementCount: z.number().int().min(0),
    completedAt: z.string().datetime({ offset: true }).nullable(),
});
/**
 * A pre-registered answer value AS THE VISITOR SUBMITS IT — the same two primitives
 * `checkInAnswerSchema` allows. The string branch is the text policy's `freeText` (handoff
 * 0009 §2): NFC + trim + whitespace collapse, no emoji/control/bidi characters. `''` stays
 * accepted (it was before — a cleared answer), which is why it is `allowEmpty`.
 *
 * WRITE PATH ONLY. Reading stored answers back must use `storedPreRegistrationAnswerValueSchema`
 * below — the policy applies on write, never on read (§1a rule 12).
 */
export const preRegistrationAnswerValueSchema = z.union([
    freeText({ max: 2000, allowEmpty: true }),
    z.boolean(),
]);
/**
 * The SHAPE of a stored pre-registration answer (read path / outbound DTOs): the pre-policy
 * bound only, so a row written before the text policy still renders exactly as stored.
 */
export const storedPreRegistrationAnswerValueSchema = z.union([z.string().max(2000), z.boolean()]);
/**
 * One invite as the admin console sees it. An ALLOW-LIST (docs/008 S7): `codeHash` is not
 * here, and neither is the plaintext code — see `issuedInviteDTOSchema`. A column added to
 * `VisitorInvite` later must not become a response field by accident.
 *
 * `status` is the DERIVED view status, so a `PENDING` row whose `expiresAt` has passed
 * reads `EXPIRED` here without any row ever having been updated to say so (INV-18).
 */
export const inviteDTOSchema = z.object({
    id: z.string().min(1),
    status: visitorInviteViewStatusSchema,
    locationId: z.string().min(1),
    locationName: z.string().min(1),
    hostPersonId: z.string().min(1),
    hostName: z.string().min(1),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    visitorEmail: z.string().min(1),
    visitorPhone: z.string().nullable(),
    purpose: z.string().nullable(),
    note: z.string().nullable(),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    redeemedAt: z.string().datetime({ offset: true }).nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    /**
     * S41 additions. The server always sends both; they are `.optional()` in the schema only so
     * a client (or fixture) built before S41 still parses and type-checks.
     */
    emailedAt: z.string().datetime({ offset: true }).nullable().optional(),
    preRegistration: invitePreRegistrationSummarySchema.optional(),
    /**
     * The visitor type the admin chose at issue time, and its label. `null` for an invite issued
     * before the type was collected, or whose type was since deleted (`SetNull`). `.optional()`
     * for the same reason as the S41 additions above: an older fixture still parses.
     */
    visitorTypeId: z.string().min(1).nullable().optional(),
    visitorTypeLabel: multiLanguageContentSchema.nullable().optional(),
    /** S48-lh45 Phase 4b. The server always sends both; optional so older fixtures still parse. */
    coHosts: z.array(inviteCoHostSchema).optional(),
    series: inviteSeriesSummarySchema.nullable().optional(),
});
/**
 * THE ONLY SHAPE THAT EVER CARRIES THE PLAINTEXT ARRIVAL CODE — the 201 body of
 * `POST /api/web/invites`, and nothing else, ever (milestone Constraint 1, ADR-0008).
 * It is a separate schema rather than an optional field on `inviteDTOSchema` precisely so
 * that "does this response carry the code?" is answerable by reading a type name, and so a
 * list endpoint physically cannot return it.
 *
 * The code is never stored, never re-readable and never logged, not even truncated. An
 * admin who loses it cancels and re-creates.
 */
export const issuedInviteDTOSchema = z.object({
    invite: inviteDTOSchema,
    code: z.string().regex(INVITE_CODE_PATTERN),
    /**
     * S41: the Visit Pass link, `${VISITOR_APP_BASE_URL}/pass#<token>`. Returned ONCE (create and
     * resend) for the admin's "copy link" and never again — only its hash is stored. Optional in
     * the schema for pre-S41 fixtures; the server always sends it.
     */
    passUrl: z.string().url().optional(),
    /**
     * Sent for a series only, first occurrence first; the top-level invite/code/passUrl are
     * occurrence 1.
     */
    occurrences: z.array(issuedInviteOccurrenceSchema).optional(),
});
/**
 * The FILTER half of `GET /api/web/invites`, with no pagination — one schema, shared by the
 * invites page and (Phase 3) the visitor log's "expected today" view, for the same reason
 * S10 made the log and its CSV export share one.
 *
 * A PLAIN `z.object` so `.merge()` below works and a cross-field refinement never turns it
 * into a ZodEffects — same reasoning as `visitFilterQuerySchema`.
 *
 * `status` accepts the DERIVED union: `PENDING` means "pending and not yet expired",
 * `EXPIRED` means "pending and expired". Both are `WHERE` clauses over `status` and
 * `expiresAt`, resolved in the repository (S11-TASK-003) — never a filter applied after the
 * fetch, which would break `meta.total`.
 *
 * `from`/`to` are ISO 8601 instants filtered half-open against `expectedFrom`: `>= from`,
 * `< to`. `q` is one bounded term matched against the visitor's and the host's name, with
 * the same `min(2)` floor as `visitFilterQuerySchema`.
 */
export const inviteFilterQuerySchema = z.object({
    status: visitorInviteViewStatusSchema.optional(),
    locationId: z.string().min(1).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    q: z.string().trim().min(2).max(100).optional(),
});
/** `GET /api/web/invites` — the shared filter, plus pagination. */
export const inviteListQuerySchema = pageQuerySchema.merge(inviteFilterQuerySchema);
/** ADD ONLY. */
export const INVITE_CANCEL_SCOPES = ['OCCURRENCE', 'SERIES'];
/** `POST /api/web/invites/:id/cancel` body. `{}` (and no body) = `OCCURRENCE`, today's behaviour. */
export const cancelInviteSchema = z.object({
    scope: z.enum(INVITE_CANCEL_SCOPES).default('OCCURRENCE'),
});
/** `POST /api/device/v1/invites/lookup`. Loose for the reason `inviteCode` above is loose. */
export const lookupInviteSchema = z.object({
    code: z.string().trim().min(1).max(32),
});
/**
 * What a paired device gets back for a valid arrival code — enough to render a check-in as
 * a confirmation rather than a form (INV-10), and nothing more.
 *
 * AN ALLOW-LIST, AND A NARROWER ONE THAN `inviteDTOSchema` (docs/008 S7). No `code`, no
 * hash, no `note` (the front desk's own `/// @pii` remark, never for a lobby screen), no
 * `visitorPhone`, no `status`, no `expiresAt` — a device that got a row back already knows
 * the invite is redeemable, and publishing the expiry instant tells a guesser nothing it
 * needs. `visitorEmail` IS included: the kiosk must echo a valid `visitorEmail` back on
 * `POST /visits`, and the address was emailed to the person now standing at the device.
 */
export const invitePrefillDTOSchema = z.object({
    inviteId: z.string().min(1),
    visitorFirstName: z.string().min(1),
    visitorLastName: z.string().min(1),
    visitorEmail: z.string().min(1),
    hostPersonId: z.string().min(1),
    hostName: z.string().min(1),
    purpose: z.string().nullable(),
    expectedFrom: z.string().datetime({ offset: true }),
    expectedTo: z.string().datetime({ offset: true }),
    /**
     * S41 ADDITIONS — OPTIONAL, because this is the published Flutter contract and an older
     * kiosk must keep parsing it. Present only when the visitor pre-registered on the Visit Pass:
     * `answers` keyed by field key, the `visitorTypeId` they chose, and the agreement version ids
     * they already signed ahead of arrival (the kiosk skips those DOCUMENT_SIGNATURE screens and
     * `POST /visits` converts them into acceptances).
     */
    answers: z.record(z.string(), storedPreRegistrationAnswerValueSchema).optional(),
    visitorTypeId: z.string().min(1).nullable().optional(),
    acceptedVersionIds: z.array(z.string().min(1)).optional(),
});
/* -------------------------------------------------------------------------- */
/* S41 — pre-registration (the Visit Pass)                                     */
/* -------------------------------------------------------------------------- */
/** `POST /api/web/invites/:id/resend` — same shape as create: the new code + link, once. */
export const resentInviteDTOSchema = issuedInviteDTOSchema;
//# sourceMappingURL=invite.js.map